import React, { useRef, useEffect, useCallback } from 'react';
import { cva } from 'class-variance-authority';
import styles from './FocusTrap.module.css';

// ── CVA definition ─────────────────────────────────────────────────────────

const focusTrap = cva(styles.base);

// ── Types ──────────────────────────────────────────────────────────────────

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

interface FocusTrapProps {
  /** Whether the trap is active */
  active?: boolean;
  /** Content to trap focus within */
  children: React.ReactNode;
  /** Called when Escape is pressed */
  onEscape?: () => void;
  /** Whether to restore focus to the previously-focused element on deactivation */
  restoreFocus?: boolean;
  /** Associates the dialog with its title element */
  'aria-labelledby'?: string;
  /**
   * Whether the trapped region is modal — i.e. the rest of the page really is
   * inert. Modal, Drawer and Popconfirm are; a transient menu that merely
   * traps focus is not, and claiming `aria-modal` there tells a screen reader
   * the document behind it is unreachable.
   */
  modal?: boolean;
}

/**
 * FocusTrap — constrains keyboard focus to its children.
 *
 * Implements circular Tab navigation, Escape handling, and optional focus
 * restoration. Uses `aria-modal` when active for screen readers.
 *
 * Used by: Modal, Drawer, Popconfirm.
 *
 * @example
 * <FocusTrap active={isOpen} onEscape={handleClose}>
 *   <dialog>...</dialog>
 * </FocusTrap>
 */
export function FocusTrap({ active = true, children, onEscape, restoreFocus = true, modal = true, 'aria-labelledby': ariaLabelledby }: FocusTrapProps) {
  const ref = useRef<HTMLDivElement>(null);
  const previousRef = useRef<HTMLElement | null>(null);

  // Store reference to previously focused element
  useEffect(() => {
    if (active) {
      previousRef.current = document.activeElement as HTMLElement | null;
    }
  }, [active]);

  // Focus first focusable child when activated
  useEffect(() => {
    if (!active || !ref.current) return;
    const first = ref.current.querySelector<HTMLElement>(FOCUSABLE);
    first?.focus();

    return () => {
      if (restoreFocus) {
        previousRef.current?.focus();
      }
    };
  }, [active, restoreFocus]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!active) return;

      if (e.key === 'Escape') {
        e.stopPropagation();
        onEscape?.();
        return;
      }

      if (e.key !== 'Tab' || !ref.current) return;

      const focusable = ref.current.querySelectorAll<HTMLElement>(FOCUSABLE);
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    },
    [active, onEscape],
  );

  return (
    <div
      ref={ref}
      onKeyDown={handleKeyDown}
      role={active && modal ? 'dialog' : undefined}
      aria-modal={(active && modal) || undefined}
      aria-labelledby={ariaLabelledby}
      className={focusTrap()}
    >
      {children}
    </div>
  );
}
